/**
 * The EVM chains the EVM Dust Cleaner sells from, shared by the page
 * (src/evm) and the Worker (worker/evm.js). Dependency-free on purpose.
 *
 * A chain is here only when all of these hold (checked 2026-10-30): Relay
 * (api.relay.link) takes deposits on it and delivers SOL; Uniswap's Calibur
 * batch executor is deployed at CALIBUR (for the gas-free mode); Multicall3
 * is at MULTICALL3; Alchemy's Portfolio API lists balances on it. zkSync
 * Era, Blast, Celo, Mode and Soneium have no Calibur; Sonic and Mantle
 * aren't in the Portfolio API.
 *
 *   rpcs        keyless public endpoints the Worker reads through, tried in
 *               order (the page never calls them itself; each answered
 *               eth_chainId on 2026-10-30)
 *   coingecko   tokens.coingecko.com list slug: the tokens a fallback scan
 *               reads balances of when Alchemy can't answer
 *   dexscreener api.dexscreener.com chain id, for the fallback scan's prices
 *   wrapped     the wrapped native coin, priced on DexScreener for the coin
 *               (chains whose coin is ETH all take Ethereum's WETH price;
 *               `nativeUsd` pins a stable coin like xDAI)
 *   l1          how the chain charges for L1 data on top of gas: "op" (OP
 *               Stack's GasPriceOracle), "scroll" (Scroll's oracle) or null
 *   usdc        Circle's USDC where Relay sells it from EIP-3009 signatures
 *               alone, with no gas at all (checked 2026-10-30; on the other
 *               chains Relay still asks for an approve first)
 *   ownGas      the chain has no EIP-7702 yet, so nothing on it can be sold
 *               gas-free: the native coin is sold in a plain transaction
 *               that pays its own gas, and tokens can't be sold. Avalanche's
 *               C-Chain (ACP-209 is still a proposal; no account there had
 *               delegation code on 2026-10-31, and Relay's dry runs failed)
 */
export const CALIBUR = "0x000000009B1D0aF20D8C6d0A44e162d11F9b8f00";
export const MULTICALL3 = "0xcA11bde05977b3631167028862bE2a173976CA11";
const OP_WETH = "0x4200000000000000000000000000000000000006";

export const CHAINS = [
  {
    id: 1, name: "Ethereum", native: "ETH", alchemy: "eth-mainnet", coingecko: "ethereum", dexscreener: "ethereum",
    rpcs: ["https://ethereum-rpc.publicnode.com", "https://eth.drpc.org", "https://rpc.mevblocker.io"], explorer: "https://etherscan.io",
    wrapped: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", l1: null, usdc: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
  },
  {
    id: 8453, name: "Base", native: "ETH", alchemy: "base-mainnet", coingecko: "base", dexscreener: "base",
    rpcs: ["https://base-rpc.publicnode.com", "https://mainnet.base.org"], explorer: "https://basescan.org",
    wrapped: OP_WETH, l1: "op", usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
  },
  {
    id: 42161, name: "Arbitrum", native: "ETH", alchemy: "arb-mainnet", coingecko: "arbitrum-one", dexscreener: "arbitrum",
    rpcs: ["https://arbitrum-one-rpc.publicnode.com", "https://arb1.arbitrum.io/rpc"], explorer: "https://arbiscan.io",
    wrapped: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1", l1: null, usdc: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
  },
  {
    id: 10, name: "Optimism", native: "ETH", alchemy: "opt-mainnet", coingecko: "optimistic-ethereum", dexscreener: "optimism",
    rpcs: ["https://optimism-rpc.publicnode.com", "https://mainnet.optimism.io"], explorer: "https://optimistic.etherscan.io",
    wrapped: OP_WETH, l1: "op", usdc: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85",
  },
  {
    id: 56, name: "BNB Chain", native: "BNB", alchemy: "bnb-mainnet", coingecko: "binance-smart-chain", dexscreener: "bsc",
    rpcs: ["https://bsc-rpc.publicnode.com", "https://bsc-dataseed.bnbchain.org"], explorer: "https://bscscan.com",
    wrapped: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c", l1: null, usdc: null,
  },
  {
    id: 137, name: "Polygon", native: "POL", alchemy: "matic-mainnet", coingecko: "polygon-pos", dexscreener: "polygon",
    rpcs: ["https://polygon-bor-rpc.publicnode.com", "https://polygon.drpc.org"], explorer: "https://polygonscan.com",
    wrapped: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270", l1: null, usdc: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
  },
  {
    id: 43114, name: "Avalanche", native: "AVAX", alchemy: "avax-mainnet", coingecko: "avalanche", dexscreener: "avalanche",
    rpcs: ["https://avalanche-c-chain-rpc.publicnode.com", "https://api.avax.network/ext/bc/C/rpc"], explorer: "https://snowtrace.io",
    wrapped: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7", l1: null, usdc: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E", ownGas: true,
  },
  {
    id: 59144, name: "Linea", native: "ETH", alchemy: "linea-mainnet", coingecko: "linea", dexscreener: "linea",
    rpcs: ["https://linea-rpc.publicnode.com", "https://rpc.linea.build"], explorer: "https://lineascan.build",
    wrapped: "0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f", l1: null, usdc: null,
  },
  {
    id: 130, name: "Unichain", native: "ETH", alchemy: "unichain-mainnet", coingecko: "unichain", dexscreener: "unichain",
    rpcs: ["https://unichain-rpc.publicnode.com", "https://mainnet.unichain.org"], explorer: "https://uniscan.xyz",
    wrapped: OP_WETH, l1: "op", usdc: null,
  },
  {
    id: 534352, name: "Scroll", native: "ETH", alchemy: "scroll-mainnet", coingecko: "scroll", dexscreener: "scroll",
    rpcs: ["https://scroll-rpc.publicnode.com", "https://rpc.scroll.io"], explorer: "https://scrollscan.com",
    wrapped: "0x5300000000000000000000000000000000000004", l1: "scroll", usdc: null,
  },
  {
    id: 100, name: "Gnosis", native: "xDAI", alchemy: "gnosis-mainnet", coingecko: "xdai", dexscreener: "gnosischain",
    rpcs: ["https://gnosis-rpc.publicnode.com", "https://rpc.gnosischain.com"], explorer: "https://gnosisscan.io",
    wrapped: "0xe91D153E0b41518A2Ce8Dd3D7944Fa863463a97d", l1: null, usdc: null, nativeUsd: 1,
  },
  {
    id: 7777777, name: "Zora", native: "ETH", alchemy: "zora-mainnet", coingecko: "zora-network", dexscreener: "zora",
    rpcs: ["https://rpc.zora.energy", "https://zora.drpc.org"], explorer: "https://explorer.zora.energy",
    wrapped: OP_WETH, l1: "op", usdc: null,
  },
  {
    id: 80094, name: "Berachain", native: "BERA", alchemy: "berachain-mainnet", coingecko: "berachain", dexscreener: "berachain",
    rpcs: ["https://berachain-rpc.publicnode.com", "https://rpc.berachain.com", "https://berachain.drpc.org"], explorer: "https://berascan.com",
    wrapped: "0x6969696969696969696969696969696969696969", l1: null, usdc: null,
  },
  {
    id: 57073, name: "Ink", native: "ETH", alchemy: "ink-mainnet", coingecko: "ink", dexscreener: "ink",
    rpcs: ["https://rpc-gel.inkonchain.com", "https://ink.drpc.org"], explorer: "https://explorer.inkonchain.com",
    wrapped: OP_WETH, l1: "op", usdc: null,
  },
  {
    id: 480, name: "World Chain", native: "ETH", alchemy: "worldchain-mainnet", coingecko: "world-chain", dexscreener: "worldchain",
    rpcs: ["https://worldchain-mainnet.g.alchemy.com/public", "https://worldchain.drpc.org"], explorer: "https://worldscan.org",
    wrapped: OP_WETH, l1: "op", usdc: null,
  },
];

export const CHAIN_BY_ID = Object.fromEntries(CHAINS.map((c) => [c.id, c]));
export const CHAIN_BY_ALCHEMY = Object.fromEntries(CHAINS.map((c) => [c.alchemy, c]));

/** Relay's ids for the destination: Solana, and native SOL on it. */
export const SOLANA_CHAIN_ID = 792703809;
export const SOL_CURRENCY = "11111111111111111111111111111111";
/** Relay's id for a chain's native coin as an origin currency. */
export const NATIVE = "0x0000000000000000000000000000000000000000";
export const RELAY_API = "https://api.relay.link";

/**
 * Where a wallet's sales gather before its one bridge to Solana: ETH on
 * Base, in the same wallet. Base's own tokens gather on Optimism instead,
 * because a sale into the same chain's coin can't be gas-free (Relay takes
 * the gas out of cross-chain fills only). ETH already on Base or Optimism
 * is bridged from where it is.
 */
export const HUB = 8453;
export const hubFor = (chainId) => (Number(chainId) === HUB ? 10 : HUB);
/** Base and Optimism: the chains a wallet bridges to SOL from (their own ETH is bridged where it is). */
export const isHub = (chainId) => Number(chainId) === HUB || Number(chainId) === hubFor(HUB);

/** Whether sales on `chainId` pay their own gas (no EIP-7702 there): only the native coin can be sold. */
export const paysOwnGas = (chainId) => !!CHAIN_BY_ID[chainId]?.ownGas;

/** Relay's explorer page for one sale. */
export const relayTxUrl = (requestId) => `https://relay.link/transaction/${requestId}`;

/** Whether `token` on `chainId` is Circle's USDC, which Relay sells from a signature alone. */
export const isGasFreeToken = (chainId, token) =>
  !!CHAIN_BY_ID[chainId]?.usdc && CHAIN_BY_ID[chainId].usdc.toLowerCase() === String(token).toLowerCase();
